import { useTranslation } from "react-i18next";
import { StyledText } from "../../../../../assets/typography/typography";
import { CircleContainer } from "../../../../../components/CircleContainer";
import { colors } from "../../../../../components/colors";
import { InfoBox } from "../../../../../components/Infobox";
import { InLineLabel } from "../../../../../components/Label";
import {
  formatDateWithYearAndTime,
  formatOrderState,
  formatOrderStateBgColor,
  formatOrderStateTextColor,
  formatOrderType,
} from "../../../../../util";
import { Order } from "../../../model/OrderSuccessResponse";
import { OrderTimelineElement } from "./OrderTimelineElement";

interface OrderTimelineProps {
  order: Order | undefined;
}

export const OrderTimeline = ({ order }: OrderTimelineProps) => {
  const { t } = useTranslation();
  let labelBackgroundColor = formatOrderStateBgColor(order?.state!);
  let labelTextColor = formatOrderStateTextColor(order?.state!);
  const orderFinalized =
    order?.state === "finalized" ||
    order?.state === "delivered" ||
    order?.state === "rejected";

  return (
    <div className="card" style={{ position: "sticky", top: 16 }}>
      <div className="card-header flex-column border-bottom-0 mt-4 justify-content-between">
        <div className="d-flex align-self-start">
          <StyledText
            color={colors.textPrimary}
            className="bodyHeaderOneSemi mb-0"
          >
            Timeline
          </StyledText>
          <InLineLabel
            backgroundColor={labelBackgroundColor}
            color={labelTextColor}
            style={{ marginLeft: "8px" }}
          >
            {formatOrderState(order?.state!, order?.deliveryType!)}
          </InLineLabel>
        </div>
        <div className="d-flex align-items-center align-self-start mt-3">
          <CircleContainer
            height="4px"
            width="4px"
            backgroundColor={
              orderFinalized ? colors.ctaInactive : colors.successMedium
            }
            style={{ marginRight: "8px" }}
          />
          <StyledText
            color={colors.textPrimary}
            className="bodySubtextRegular mb-0"
          >
            {orderFinalized ? "Order finalized" : "Order in progress"}
          </StyledText>
        </div>
      </div>
      <div className="card-body">
        <OrderTimelineElement
          title="Order placed at"
          time={t("order_details.date", {
            date: formatDateWithYearAndTime(order?.placedAt!),
          })}
          isFirstStep={true}
          isLastStep={false}
          isInProgress={order?.state === "paid"}
        >
          {order?.deliveryType !== "none" && (
            <InfoBox
              className="p-3 flex-column mt-2"
              backgroundColor={colors.ctaInactive}
              style={{ gap: "16px" }}
            >
              <StyledText
                color={colors.textPrimary}
                className="bodyTextRegular mb-0"
              >
                Time requested for:{" "}
                <span className="bodyTextSemibold mb-0">
                  {order?.preOrderFor
                    ? formatDateWithYearAndTime(order?.preOrderFor)
                    : "ASAP"}
                </span>
              </StyledText>
            </InfoBox>
          )}
        </OrderTimelineElement>

        {order?.deliveryType === "none" ? (
          <>
            <OrderTimelineElement
              title={formatOrderType(order?.deliveryType).finalState + " at"}
              time={t("order_details.date", {
                date: formatDateWithYearAndTime(order?.deliveredAt!),
              })}
              isFirstStep={false}
              isLastStep={true}
              isInProgress={false}
            />
          </>
        ) : (
          <>
            {order?.acceptedAt !== undefined && (
              <OrderTimelineElement
                title="Accepted at"
                time={t("order_details.date", {
                  date: formatDateWithYearAndTime(order?.acceptedAt!),
                })}
                isFirstStep={false}
                isLastStep={false}
                isInProgress={
                  order?.state === "accepted" ||
                  order?.state === "accepted.pre_order"
                }
              >
                {order?.estimatedPreparationTime !== undefined && (
                  <InfoBox
                    className="p-3 flex-column mt-2"
                    backgroundColor={colors.ctaInactive}
                    style={{ gap: "16px" }}
                  >
                    <StyledText
                      color={colors.textPrimary}
                      className="bodyTextRegular mb-0"
                    >
                      Prep time:{" "}
                      <span className="bodyTextSemibold mb-0">
                        {Math.round(order?.estimatedPreparationTime! / 60) +
                          " minutes"}
                      </span>
                    </StyledText>
                  </InfoBox>
                )}
              </OrderTimelineElement>
            )}
            {order?.cookingAt !== undefined && (
              <OrderTimelineElement
                title="In cooking at"
                time={t("order_details.date", {
                  date: formatDateWithYearAndTime(order?.cookingAt!),
                })}
                isFirstStep={false}
                isLastStep={false}
                isInProgress={order?.state === "manufacturing"}
              />
            )}
            {order?.readyAt !== undefined && (
              <OrderTimelineElement
                title="Ready at"
                time={t("order_details.date", {
                  date: formatDateWithYearAndTime(order?.readyAt!),
                })}
                isFirstStep={false}
                isLastStep={false}
                isInProgress={order?.state === "ready"}
              />
            )}
            {order?.inDeliveryAt !== undefined && (
              <OrderTimelineElement
                title="In delivery at"
                time={t("order_details.date", {
                  date: formatDateWithYearAndTime(order?.inDeliveryAt!),
                })}
                isFirstStep={false}
                isLastStep={false}
                isInProgress={order?.state === "in_delivery"}
              />
            )}
            {order?.deliveredAt !== undefined && (
              <OrderTimelineElement
                title={formatOrderType(order?.deliveryType).finalState + " at"}
                time={t("order_details.date", {
                  date: formatDateWithYearAndTime(order?.deliveredAt!),
                })}
                isFirstStep={false}
                isLastStep={true}
                isInProgress={false}
              />
            )}
            {order?.rejectedAt !== undefined && (
              <OrderTimelineElement
                title="Rejected at"
                time={t("order_details.date", {
                  date: formatDateWithYearAndTime(order?.rejectedAt!),
                })}
                isFirstStep={false}
                isLastStep={true}
                isInProgress={false}
                isRejected={true}
              >
                {order?.rejectedReason !== undefined && (
                  <InfoBox
                    className="p-3 flex-column mt-2"
                    backgroundColor={colors.ctaInactive}
                    style={{ gap: "16px" }}
                  >
                    <StyledText
                      color={colors.textPrimary}
                      className="bodyTextRegular mb-0"
                    >
                      Reason:{" "}
                      <span className="bodyTextSemibold mb-0">
                        {order?.rejectedReason}
                      </span>
                    </StyledText>
                  </InfoBox>
                )}
              </OrderTimelineElement>
            )}
          </>
        )}
      </div>
    </div>
  );
};
